import type { Character } from '@/schemas/character';
import type { LevelUpStepId } from './types';

/**
 * **Routing puro** degli step del flusso di salita di livello. Estratto da
 * `LevelUpFlowSteps.tsx` (PR 8 Fase 2) per:
 *  - Esposizione a `<LevelUpFlowPage>` che ora pilota `<StepNav>` esterno
 *  - Rispetto della regola ESLint `react-refresh/only-export-components`
 *    (i file .tsx non possono esportare sia componenti che helper puri)
 *
 * La sequenza è dinamica: gli step `granted` / `multiclass` / `archetype` /
 * `spells` sono visibili solo se i flag `needs*Step === true`. Lo step
 * `pastTen` appare solo per il passaggio L10 → L11.
 */

export interface StepFlowFlags {
  needsPastTenStep: boolean;
  needsGrantedStep: boolean;
  needsMulticlassStep: boolean;
  needsArchetypeStep: boolean;
  needsSpellsStep: boolean;
}

/**
 * Decide il primo step dopo `intro`:
 *  - `'pastTen'` se PG sta passando da L10 a L11 (scelta cruciale)
 *  - `'move'` se PG è Apprendista/Instancabile già scelto (no stat aumento)
 *  - `'stat'` per il flow standard L2-L10.
 */
export function pickIntroNextStep(character: Character, flags: StepFlowFlags): LevelUpStepId {
  if (flags.needsPastTenStep) return 'pastTen';
  if (character.pastTen === 'apprendista' || character.pastTen === 'instancabile') {
    return 'move';
  }
  return 'stat';
}

/**
 * Sequenza dopo lo step `move`: granted → multiclass → archetype → spells →
 * confirm. Gli step disabilitati dai flag vengono saltati.
 */
export function pickNextStep(
  from: 'move' | 'granted' | 'multiclass' | 'archetype',
  flags: StepFlowFlags,
): LevelUpStepId {
  const sequence: LevelUpStepId[] = ['move'];
  if (flags.needsGrantedStep || from === 'granted') sequence.push('granted');
  if (flags.needsMulticlassStep || from === 'multiclass') sequence.push('multiclass');
  if (flags.needsArchetypeStep || from === 'archetype') sequence.push('archetype');
  if (flags.needsSpellsStep) sequence.push('spells');
  sequence.push('confirm');
  const currentIdx = sequence.indexOf(from);
  return sequence[currentIdx + 1] ?? 'confirm';
}

/**
 * Step precedente al `confirm`: spells > archetype > multiclass > granted > move
 * per priorità di ritorno (l'utente torna allo step più vicino disponibile).
 */
export function pickConfirmPrevStep(flags: StepFlowFlags): LevelUpStepId {
  if (flags.needsSpellsStep) return 'spells';
  if (flags.needsArchetypeStep) return 'archetype';
  if (flags.needsMulticlassStep) return 'multiclass';
  if (flags.needsGrantedStep) return 'granted';
  return 'move';
}

export function pickArchetypeBackStep(flags: StepFlowFlags): LevelUpStepId {
  if (flags.needsMulticlassStep) return 'multiclass';
  if (flags.needsGrantedStep) return 'granted';
  return 'move';
}

export function pickSpellsBackStep(flags: StepFlowFlags): LevelUpStepId {
  if (flags.needsArchetypeStep) return 'archetype';
  if (flags.needsMulticlassStep) return 'multiclass';
  if (flags.needsGrantedStep) return 'granted';
  return 'move';
}

/**
 * Step precedente a `move`: dipende dal flow standard vs L10 → L11.
 * Se `needsPastTenStep`, lo step precedente è `pastTen` (stat skippato per
 * Apprendista/Instancabile). Altrimenti `stat`.
 */
export function pickMoveBackStep(flags: StepFlowFlags): LevelUpStepId {
  return flags.needsPastTenStep ? 'pastTen' : 'stat';
}

/**
 * Step precedente a `stat`: sempre `intro` (lo stat è il primo step
 * decisionale dopo l'intro nel flow L2-L10 standard).
 */
export function pickStatBackStep(): LevelUpStepId {
  return 'intro';
}

/**
 * Step precedente a `intro`: nessuno (intro è il primo). Convenzione: `intro`.
 */
export function pickIntroBackStep(): LevelUpStepId {
  return 'intro';
}

/**
 * Lista di tutti gli step applicabili al flow corrente (per `<StepRail>`).
 * Replica la logica già usata da `<StepNavigator>` di `LevelUpFlowPage`
 * pre-PR 8.
 */
export function getApplicableSteps(flags: StepFlowFlags): readonly LevelUpStepId[] {
  const steps: LevelUpStepId[] = ['intro'];
  if (flags.needsPastTenStep) steps.push('pastTen');
  steps.push('stat', 'move');
  if (flags.needsGrantedStep) steps.push('granted');
  if (flags.needsMulticlassStep) steps.push('multiclass');
  if (flags.needsArchetypeStep) steps.push('archetype');
  if (flags.needsSpellsStep) steps.push('spells');
  steps.push('confirm');
  return steps;
}

export const STEP_LABELS: Record<LevelUpStepId, string> = {
  intro: 'Intro',
  pastTen: 'Scelta cruciale',
  stat: '+1 Caratteristica',
  move: 'Mossa',
  granted: 'Sotto-scelta',
  multiclass: 'Multiclasse',
  archetype: '2° Archetipo',
  spells: 'Incantesimi',
  confirm: 'Conferma',
};
